import { supabase } from "@/integrations/supabase/client";

export { supabase };
let subscriptionCounter = 0;

let cachedAccessToken: string | null = null;
let cachedTokenExpiresAt = 0;

supabase.auth.onAuthStateChange((_event, session) => {
  if (session) {
    cachedAccessToken = session.access_token;
    cachedTokenExpiresAt = (session.expires_at ?? 0) * 1000;
  } else {
    cachedAccessToken = null;
    cachedTokenExpiresAt = 0;
  }
});

async function getCachedAccessToken(): Promise<string> {
  if (cachedAccessToken && Date.now() < cachedTokenExpiresAt - 30_000) {
    return cachedAccessToken;
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Sessão expirada. Faça login novamente.");
  cachedAccessToken = session.access_token;
  cachedTokenExpiresAt = (session.expires_at ?? 0) * 1000;
  return session.access_token;
}

const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function debouncedInvalidate(
  queryClient: { invalidateQueries: (opts: { queryKey: unknown[] }) => void },
  queryKey: unknown[],
  delayMs = 400
) {
  const key = JSON.stringify(queryKey);
  const existing = debounceTimers.get(key);
  if (existing) clearTimeout(existing);
  debounceTimers.set(
    key,
    setTimeout(() => {
      debounceTimers.delete(key);
      queryClient.invalidateQueries({ queryKey });
    }, delayMs)
  );
}

export function tenantQuery(table: string, tenantId: string) {
  return supabase.from(table).select().eq("tenant_id", tenantId);
}

export function tenantFrom(table: string, tenantId: string) {
  return {
    select: (columns = "*") =>
      supabase.from(table).select(columns).eq("tenant_id", tenantId),
    insert: (data: Record<string, unknown> | Record<string, unknown>[]) => {
      const withTenant = Array.isArray(data)
        ? data.map((d) => ({ ...d, tenant_id: tenantId }))
        : { ...data, tenant_id: tenantId };
      return supabase.from(table).insert(withTenant);
    },
    update: (data: Record<string, unknown>) =>
      supabase.from(table).update(data).eq("tenant_id", tenantId),
    delete: () => supabase.from(table).delete().eq("tenant_id", tenantId),
    upsert: (data: Record<string, unknown> | Record<string, unknown>[]) => {
      const withTenant = Array.isArray(data)
        ? data.map((d) => ({ ...d, tenant_id: tenantId }))
        : { ...data, tenant_id: tenantId };
      return supabase.from(table).upsert(withTenant);
    },
  };
}

export async function getAuthSession() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Não autenticado");
  return session;
}

export async function fetchEdgeFunction(
  functionPath: string,
  options?: RequestInit & { timeout?: number }
) {
  const initialToken = await getCachedAccessToken();

  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const { timeout: timeoutMs = 30_000, method = "POST", body: rawBody, headers: rawHeaders, ...rest } = options ?? {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const callEdge = async (token: string) => {
      const response = await fetch(`${baseUrl}/functions/v1/${functionPath}`, {
        ...rest,
        method,
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...Object.fromEntries(new Headers(rawHeaders ?? {}).entries()),
          apikey: apiKey,
          Authorization: `Bearer ${token}`,
        },
        body: rawBody,
      });
      const data = await response.json().catch(() => ({}));
      return { response, data };
    };

    let token = initialToken;
    let { response, data } = await callEdge(token);

    const firstErrorMessage = String(data?.error || data?.msg || data?.message || data?.details || "");
    const normalizedError = firstErrorMessage.toLowerCase();
    const shouldRetryForJwt =
      (response.status === 401 || response.status === 403) &&
      (
        normalizedError.includes("invalid jwt") ||
        normalizedError.includes("jwt") ||
        normalizedError.includes("unauthorized") ||
        normalizedError.includes("token")
      );

    if (shouldRetryForJwt) {
      const {
        data: { session: refreshedSession },
      } = await supabase.auth.refreshSession();
      if (!refreshedSession) throw new Error("Sessão expirada. Faça login novamente.");
      token = refreshedSession.access_token;
      ({ response, data } = await callEdge(token));
    }

    if (!response.ok) {
      const msg = data?.error || data?.msg || data?.message || data?.details || `Erro ${response.status}`;
      throw new Error(msg);
    }
    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("Tempo limite excedido. Tente novamente.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function fetchPublicEdgeFunction(
  functionPath: string,
  options?: RequestInit
) {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  return fetch(`${baseUrl}/functions/v1/${functionPath}`, {
    ...options,
    headers: {
      ...options?.headers,
      "Content-Type": "application/json",
      apikey: apiKey,
    },
  }).then(async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || "Erro desconhecido");
    }
    return data;
  });
}

export function subscribeToChannel(
  channelName: string,
  table: string,
  filter: string,
  events: Array<"INSERT" | "UPDATE" | "DELETE" | "*">,
  callback: (payload?: Record<string, unknown>) => void,
  onStatus?: (status: string, error?: Error) => void
) {
  subscriptionCounter += 1;
  const uniqueChannelName = `${channelName}-${subscriptionCounter}`;
  let channel = supabase.channel(uniqueChannelName);

  for (const event of events) {
    const config = filter
      ? { event, schema: "public" as const, table, filter }
      : { event, schema: "public" as const, table };
    channel = channel.on("postgres_changes", config, callback);
  }

  channel.subscribe((status, err) => {
    onStatus?.(status, err);
  });

  return () => {
    supabase.removeChannel(channel);
  };
}
